import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, ArrowRight, Loader2, Home, CheckCircle, ShieldCheck, Building2, Sparkles } from 'lucide-react';
import { pmsFrom, pmsAuth } from '@/integrations/pms';
import { Button } from '@/components/ui/button';
import { useAutoHeroImage } from '@/hooks/useAutoHeroImage';
import { usePageDesign } from '@/hooks/usePageDesign';
import { useBrandTrustBadges } from '@/hooks/useBrandTrustBadges';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from '@/components/seo/SEOHead';
import { LeadForm } from '@/components/forms/LeadForm';
import { ConfigurableHero } from '@/components/common/ConfigurableHero';
import { ConfigurableLayout } from '@/components/common/ConfigurableLayout';
import DynamicIcon from '@/components/common/DynamicIcon';
import { sanitizeHtml } from '@/lib/sanitize';
import heroImage from '@/assets/hero-trades.jpg';
import { useIndustrySlug } from '@/hooks/useIndustrySlug';

const ServiceRegionPage: React.FC = () => {
  const params = useParams<{ regionSlug?: string; a?: string; b?: string }>();
  const regionSlug = params.regionSlug || params.b;
  const industrySlug = useIndustrySlug();

  const { data: region, isLoading: regionLoading } = useQuery({
    queryKey: ['service-region', regionSlug],
    queryFn: async () => {
      const { data, error } = await pmsFrom('service_regions')
        .select('*')
        .eq('slug', regionSlug)
        .eq('is_active', true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!regionSlug,
  });

  const { data: locations = [], isLoading: locsLoading } = useQuery({
    queryKey: ['region-locations', region?.id],
    queryFn: async () => {
      const { data } = await pmsFrom('service_locations')
        .select('id, name, slug, description, is_primary, phone, email, featured_image')
        .eq('region_id', region!.id)
        .eq('is_published', true)
        .order('sort_order');
      return data || [];
    },
    enabled: !!region?.id,
  });

  const { data: businessSettings } = useQuery({
    queryKey: ['business-settings-region'],
    queryFn: async () => {
      const { data } = await pmsFrom('business_settings')
        .select('company_name, phone, email, industry, address_state, license_number, years_experience')
        .limit(1)
        .maybeSingle();
      return data;
    },
  });

  const companyName = businessSettings?.company_name || 'Our Company';
  const rawIndustry = businessSettings?.industry || 'services';
  const stateAbbr = businessSettings?.address_state || '';
  const effectivePhone = region?.phone || businessSettings?.phone || '';
  const effectiveEmail = region?.email || businessSettings?.email || '';

  // Industry display
  const industryLabelMap: Record<string, string> = {
    hvac: 'HVAC', plumbing: 'Plumbing', electrical: 'Electrical', roofing: 'Roofing',
    painting: 'Painting', flooring: 'Flooring', remodeling: 'Remodeling', landscaping: 'Landscaping',
    cleaning: 'Cleaning', pest_control: 'Pest Control', pool_spa: 'Pool & Spa', handyman: 'Handyman',
    solar: 'Solar', fencing: 'Fencing', concrete: 'Concrete & Masonry',
  };
  const formatIndustryName = (slug: string) =>
    industryLabelMap[slug.trim()] || slug.trim().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const industryList = rawIndustry.split(',').map(formatIndustryName);
  const industryDisplay = industryList.length > 1
    ? `${industryList.slice(0, -1).join(', ')} & ${industryList[industryList.length - 1]}`
    : industryList[0];

  // Auto-generate hero image if none exists and setting is enabled
  const { isGenerating: isGeneratingHero, generate: generateHero } = useAutoHeroImage({
    entityType: 'region',
    entityId: region?.id,
    entityName: region?.name,
    currentImage: region?.featured_image,
    industry: rawIndustry,
    state: stateAbbr,
  });

  // Check if user is logged in (admin) to show generate button
  const { data: session } = useQuery({
    queryKey: ['auth-session-hero'],
    queryFn: async () => {
      const session = await pmsAuth.getSession();
      return session;
    },
    staleTime: 1000 * 60 * 5,
  });

  // Page design config
  const { heroStyle, layoutVariant, showBreadcrumbs, showTrustBadges, showCtaButtons, showFaqSection, showLeadForm, isLoading: designLoading } = usePageDesign('region');

  // Trust badges -- sourced from Brand Intelligence when available, fallback to businessSettings
  const trustBadges = useBrandTrustBadges(businessSettings);

  if (regionLoading || designLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!region) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-heading font-bold mb-4">Region Not Found</h1>
        <p className="text-muted-foreground mb-6">This region doesn't exist or isn't active.</p>
        <Button asChild>
          <Link to={`/${industrySlug}`}>View All Service Areas</Link>
        </Button>
      </div>
    );
  }

  // Sidebar content
  const sidebarContent = (
    <>
      {/* CTA Card */}
      <div className="bg-primary text-primary-foreground rounded-xl p-6 space-y-4">
        <h3 className="font-heading text-xl font-bold">Need {industryDisplay}?</h3>
        <p className="text-primary-foreground/80 text-sm">
          {companyName} serves the entire {region.name} region.
        </p>
        {effectivePhone && (
          <Button asChild variant="secondary" className="w-full gap-2">
            <a href={`tel:${effectivePhone.replace(/\D/g, '')}`}>
              <Phone className="h-4 w-4" />
              Call {effectivePhone}
            </a>
          </Button>
        )}
        {effectiveEmail && (
          <a
            href={`mailto:${effectiveEmail}`}
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg border border-primary-foreground/30 text-primary-foreground text-sm hover:bg-primary-foreground/10 transition-colors"
          >
            <Mail className="h-4 w-4" />
            {effectiveEmail}
          </a>
        )}
        <Button asChild variant="outline" className="w-full border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
          <Link to="/contact">Request Free Estimate</Link>
        </Button>
      </div>
      <AllRegionsSidebar currentSlug={regionSlug || ''} />
    </>
  );

  // Main content
  const mainContent = (
    <>
      {region.content && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="prose prose-lg max-w-none dark:prose-invert seo-content"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(region.content) }}
        />
      )}

      {/* Location Cards */}
      <section>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h2 className="font-heading text-2xl font-bold mb-2">Communities We Serve in {region.name}</h2>
          <p className="text-muted-foreground mb-6">
            Click any community to see our full range of {industryDisplay.toLowerCase()} services available in that area.
          </p>
        </motion.div>

        {locsLoading ? (
          <div className="grid sm:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : locations.length === 0 ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-card rounded-xl border border-border p-8 text-center">
            <Building2 className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-lg mb-2">Communities Coming Soon</h3>
            <p className="text-muted-foreground mb-4">
              We're expanding our service coverage in the {region.name} area. Contact us to learn if we can serve your location.
            </p>
            <Button asChild><Link to="/contact">Contact Us</Link></Button>
          </motion.div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {locations.map((loc, index) => (
              <motion.div
                key={loc.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.03 }}
                viewport={{ once: true }}
              >
                <Link
                  to={`/${industrySlug}/${regionSlug}/${loc.slug}`}
                  className="group block bg-card p-5 rounded-xl border border-border hover:border-primary/50 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary transition-colors duration-300">
                      <MapPin className="h-5 w-5 text-primary group-hover:text-primary-foreground transition-colors duration-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-heading font-semibold text-base group-hover:text-primary transition-colors truncate">{loc.name}</h3>
                        {loc.is_primary && <Badge variant="default" className="text-[10px] flex-shrink-0">HQ</Badge>}
                      </div>
                      {loc.description && <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{loc.description}</p>}
                      {(loc.phone || loc.email) && (
                        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mb-2">
                          {loc.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{loc.phone}</span>}
                          {loc.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{loc.email}</span>}
                        </div>
                      )}
                      <span className="inline-flex items-center text-sm font-medium text-primary">
                        View Details <ArrowRight className="h-3.5 w-3.5 ml-1" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Lead Form */}
      {showLeadForm && (
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-card rounded-xl border border-border p-8">
          <h2 className="font-heading text-2xl font-bold mb-2">Get Service in {region.name}</h2>
          <p className="text-muted-foreground mb-6">Fill out the form below and we'll connect you with our team serving the {region.name} area.</p>
          <LeadForm source={`service-region-${region.slug}`} />
        </motion.section>
      )}
    </>
  );

  return (
    <>
      <SEOHead
        title={region.meta_title || `${industryDisplay} in ${region.name} ${stateAbbr} | ${companyName}`}
        description={region.meta_description || `Professional ${industryDisplay.toLowerCase()} services in the ${region.name} area. ${companyName} serves ${locations.length} communities across the ${region.name} region.`}
      />

      <ConfigurableHero
        heroStyle={heroStyle}
        title={`${region.name}'s Trusted ${industryDisplay} Experts.`}
        subtitle={region.description || `${companyName} proudly serves ${locations.length} communities across the ${region.name} region with reliable, professional ${industryDisplay.toLowerCase()} services.`}
        backgroundImage={region.featured_image || heroImage}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="h-4 w-4" /> },
          { label: 'Service Areas', href: `/${industrySlug}`, icon: <MapPin className="h-4 w-4" /> },
          { label: region.name },
        ]}
        showBreadcrumbs={showBreadcrumbs}
        trustBadges={trustBadges}
        showTrustBadges={showTrustBadges}
        showCtaButtons={showCtaButtons}
        phone={effectivePhone}
      >
        {session && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-4">
            <Button size="sm" variant="outline" className="gap-2 text-xs opacity-70 hover:opacity-100" onClick={generateHero} disabled={isGeneratingHero}>
              {isGeneratingHero ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {isGeneratingHero ? 'Generating...' : 'Generate Hero Image'}
            </Button>
          </motion.div>
        )}
      </ConfigurableHero>

      <ConfigurableLayout
        layout={layoutVariant}
        main={mainContent}
        sidebar={sidebarContent}
      />
    </>
  );
};

const AllRegionsSidebar: React.FC<{ currentSlug: string }> = ({ currentSlug }) => {
  const industrySlug = useIndustrySlug();
  const { data: regions = [] } = useQuery({
    queryKey: ['all-regions-sidebar'],
    queryFn: async () => {
      const { data } = await pmsFrom('service_regions')
        .select('name, slug, icon')
        .eq('is_active', true)
        .order('sort_order');
      return data || [];
    },
  });

  if (regions.length === 0) return null;

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
        <MapPin className="h-5 w-5 text-primary" />
        All Regions
      </h3>
      <ul className="space-y-1">
        {regions.map(r => (
          <li key={r.slug}>
            <Link
              to={`/${industrySlug}/${r.slug}`}
              className={`flex items-center gap-2 py-2 px-3 rounded-lg text-sm transition-colors ${
                r.slug === currentSlug
                  ? 'bg-primary/10 text-primary font-semibold'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <DynamicIcon name={r.icon || 'MapPin'} className="h-4 w-4 flex-shrink-0" />
              {r.name}
            </Link>
          </li>
        ))}
      </ul>
      <Button asChild variant="ghost" size="sm" className="w-full mt-3">
        <Link to={`/${industrySlug}`}>View All Areas</Link>
      </Button>
    </div>
  );
};

export default ServiceRegionPage;
